import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import HeaderWithMenu from "../components/Common/HeaderWithMenu.tsx";
import { AppointmentDetailCard } from "../components/Appointment/AppointmentDetailCard.tsx";
import TitleWithBody from "../components/Common/TitleWithBody.tsx";
import { Appointment } from "../Types/index.ts";
import Api from "../Interceptor/Api/index.ts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RecommendedServices from "../components/Appointment/RecommendedServices.tsx";
import AppointmentDocuments from "../components/Appointment/AppointmentDocuments.tsx";
import { ToastContainer, toast } from "react-toastify";
import AppointementAdviceText from "../Helper/AppointmentAdviceText.ts";
import { Constants } from "../Constants/Constants.js";
import { WebRoutes } from "../Routes/WebRoutes.js";
import { UpcomingAppointmentInformation } from "../components/Appointment/UpcomingAppointmentInformation.tsx";
import { UpcomingAppointmentInputInstructions } from "../components/Appointment/UpcomingAppointmentInputInstructions.tsx";

export default function AppointmentDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const [appointmentDetails, setAppointmentDetails] =
    React.useState<Appointment>({} as Appointment);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [instructionsStatus, setInstructionsStatus] = useState(
    sessionStorage.getItem(appointmentDetails?.id) || "idle"
  );

  const fetchAppointmentDetails = async () => {
    setLoading(true);
    try {
      const response = await Api.appointment.getAppointmentDetails(params?.id);
      setAppointmentDetails(response);
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setError(error?.message || "Failed to fetch appointment details");
    } finally {
      setLoading(false);
    }
  };

  const addAppointmentInstructions = async (instructions: string) => {
    const requestBody = {
      id: appointmentDetails?.id,
      appointment: {
        customer_instructions: instructions,
      },
    };

    console.log(requestBody);

    try {
      await Api.appointment.sendAppointmentInstructions(requestBody);
      toast.success("Instructions added successfully");
      setInstructionsStatus("success");
      sessionStorage.setItem(appointmentDetails?.id, "success");
    } catch (error) {
      toast.error(error?.message);
      console.error("Error adding appointment instructions:", error);
    }
  };

  const downloadInvoice = async () => {
    try {
      const response = await Api.appointment.getInvoicePDF(params?.id);
      const downloadUrl = response?.invoice_pdf?.image_url;
      window.open(downloadUrl, "_blank");
    } catch (error) {
      toast.error(error?.message);
      console.error("Error downloading invoice:", error);
    }
  };

  React.useEffect(() => {
    fetchAppointmentDetails();
  }, []);

  const location = useLocation();

  React.useEffect(() => {
    toast.dismiss();
    setInstructionsStatus(
      sessionStorage?.getItem(appointmentDetails?.id) || "idle"
    );
  }, [location, appointmentDetails]);

  React.useEffect(() => {
    const userId =
      localStorage.getItem("user_id") || sessionStorage.getItem("user_id");

    if (!userId) {
      navigate(WebRoutes.BOOKING_STEP_ONE);
    }
  }, [navigate]);

  const adviceText = AppointementAdviceText(
    appointmentDetails?.doctor?.user_full_name,
    appointmentDetails?.estimated_recovery_date,
    appointmentDetails?.secondary_services
  );

  const doctorRecommendedServices =
    (appointmentDetails?.secondary_services ?? []).filter(
      (service) => service?.doctor_prescribed
    ).length > 0;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {/* Header and Menu */}
        <HeaderWithMenu />
        <p>No appointment details found.</p>
        <p />
      </Box>
    );
  }

  return (
    <Box
      className="form-container-v1"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        gap: 2,
        padding: "0px",
      }}
    >
      {/* Header and Menu */}
      <HeaderWithMenu />
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          width: "100%",
          padding: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            gap: 2,
          }}
          padding={"20px"}
        >
          {/* Appointment Detail Card */}
          <AppointmentDetailCard data={appointmentDetails} />

          {appointmentDetails?.status === "upcoming" && (
            <>
              <UpcomingAppointmentInformation width="100%" />
              <UpcomingAppointmentInputInstructions
                instructionStatus={instructionsStatus}
                onSubmit={addAppointmentInstructions}
              />
            </>
          )}

          {/* Doctor's Consultation */}
          {adviceText.length > 0 && (
            <TitleWithBody title="Doctor’s  Advice" subtitle={adviceText} />
          )}

          {/* Recommended Services */}
          {doctorRecommendedServices && (
            <RecommendedServices
              title="Service Prescribed"
              appointmentId={appointmentDetails?.id}
              recommendedServices={appointmentDetails?.secondary_services ?? []}
              onTap={(selectedOptions) => {}}
              buttonTitle="Request to Book Service"
            />
          )}
        </Box>
      </Box>
      {/* Invoice */}
      {appointmentDetails?.status === Constants.AppointmentStatus.COMPLETED && (
        <Box
          sx={{
            width: "100%",
            padding: "12px",
            position: "sticky",
            bottom: 0,
            background: "white",
            zIndex: 10,
          }}
        >
          <AppointmentDocuments
            sx={{
              marginBottom: "20px",
            }}
            onButtonTap={downloadInvoice}
          />
        </Box>
      )}
      <ToastContainer />
    </Box>
  );
}
